import type { DamageContext } from '../characters';

export type CharacterHealthChangeCause = 'damage' | 'heal' | 'set';

export interface CharacterHealthChangeEvent {
  readonly characterId: string;
  readonly configId: string;
  readonly previousHealth: number;
  readonly currentHealth: number;
  readonly maxHealth: number;
  readonly delta: number;
  readonly cause: CharacterHealthChangeCause;
  readonly damageContext?: DamageContext;
  readonly metadata?: Record<string, unknown>;
}

// Lifecycle actions are lightweight intents used to drive downstream systems.
export type CharacterLifecycleAction =
  | { type: 'play-hit'; animation: string }
  | { type: 'play-death'; animation?: string }
  | { type: 'despawn'; mode: 'immediate' | 'after-animation'; delaySeconds?: number };

export interface CharacterLifecycleScript {
  readonly id?: string;
  onHealthChanged?(event: CharacterHealthChangeEvent): CharacterLifecycleAction | CharacterLifecycleAction[] | void;
}

export type CharacterLifecycleScriptRef = CharacterLifecycleScript | (() => CharacterLifecycleScript);

export const normalizeLifecycleScript = (source: CharacterLifecycleScriptRef): CharacterLifecycleScript =>
  typeof source === 'function' ? source() : source;

export const composeLifecycleScripts = (
  ...refs: CharacterLifecycleScriptRef[]
): CharacterLifecycleScript[] => refs.map(normalizeLifecycleScript);

export const runLifecycleScripts = (
  refs: readonly CharacterLifecycleScriptRef[],
  event: CharacterHealthChangeEvent
): CharacterLifecycleAction[] => {
  const actions: CharacterLifecycleAction[] = [];
  const scripts = composeLifecycleScripts(...refs);
  for (const script of scripts) {
    try {
      const result = script.onHealthChanged?.(event);
      if (!result) {
        continue;
      }
      if (Array.isArray(result)) {
        actions.push(...result);
      } else {
        actions.push(result);
      }
    } catch (_error) {
      // Ignore script errors to keep lifecycle processing resilient.
    }
  }
  return actions;
};

export { createBasicLifecycleScript, type BasicLifecycleScriptOptions } from './defaults';
